// Publicar un lanzamiento en Mercado Libre: lo que el front le pide a atlas-backend
// (ADR-2026-09-30-la-ficha-de-mercado-libre). Todo lo hace el backend con el token de la conexión.

import { atlasBackendFetch, atlasBackendGet } from "./atlasBackend";
import { codigoDeError, ErrorDeMercadoLibre } from "./mercadoLibreConexionLogica";
import { pideColorDeLaRespuesta, type AtributoConfirmado, type AtributoQuePide } from "./mercadoLibrePublicarLogica";
import { exigirPermisoDeTienda } from "./permisosDelEquipo";

export * from "./mercadoLibrePublicarLogica";

async function pedir<T>(fn: () => Promise<Response>): Promise<T> {
  let r: Response;
  try {
    r = await fn();
  } catch {
    throw new ErrorDeMercadoLibre("red");
  }
  let cuerpo: unknown = null;
  try {
    cuerpo = await r.json();
  } catch {
    /* sin cuerpo */
  }
  if (!r.ok) {
    const e = new ErrorDeMercadoLibre(codigoDeError(r.status, cuerpo));
    // El mensaje del backend, siempre que venga: dice qué pasó (un rechazo de Mercado Libre, un 500 con
    // "probá de nuevo"). El texto por código era de la conexión ("no terminó de conectar la cuenta") y
    // confundía en la ficha (prueba del 2/10, crear una guía de talles).
    const msg = (cuerpo as { message?: unknown } | null)?.message;
    if (typeof msg === "string" && msg.trim()) e.message = msg;
    throw e;
  }
  return cuerpo as T;
}

const conMarca = (brandId: string | null) => (brandId ? `&brand_id=${encodeURIComponent(brandId)}` : "");

export interface CategoriaSugerida {
  categoria_id: string;
  categoria: string;
  dominio_id: string | null;
  dominio: string | null;
}

export async function sugerirCategorias(nombre: string): Promise<CategoriaSugerida[]> {
  const r = await pedir<{ categorias: CategoriaSugerida[] }>(() => atlasBackendGet(`/v1/mercadolibre/categorias?q=${encodeURIComponent(nombre)}`));
  return r.categorias ?? [];
}

export async function atributosDeLaCategoria(categoriaId: string): Promise<{ atributos: AtributoQuePide[]; pideGuia: boolean; pideColor: boolean | undefined }> {
  const r = await pedir<{ atributos: AtributoQuePide[]; pide_guia_de_talles: boolean; pide_color?: boolean }>(() =>
    atlasBackendGet(`/v1/mercadolibre/categorias/${encodeURIComponent(categoriaId)}/atributos`),
  );
  return { atributos: r.atributos ?? [], pideGuia: r.pide_guia_de_talles === true, pideColor: pideColorDeLaRespuesta(r.pide_color) };
}

export async function sugerirAtributos(p: {
  nombre: string;
  descripcion: string | null;
  categoria: string | null;
  atributos: AtributoQuePide[];
  fotos: string[];
}): Promise<AtributoConfirmado[]> {
  const r = await pedir<{ atributos: AtributoConfirmado[] }>(() =>
    atlasBackendFetch("/v1/mercadolibre/atributos/sugerir", {
      nombre: p.nombre,
      descripcion: p.descripcion,
      categoria: p.categoria,
      atributos: p.atributos,
      file_names: p.fotos.slice(0, 4),
    }),
  );
  return r.atributos ?? [];
}

export interface GuiaResumen {
  id: string;
  nombre: string;
  tipo: string;
}
export interface GuiaDeTalles extends GuiaResumen {
  filas: Array<{ id: string; talle: string; medidas: Record<string, string> }>;
}
export interface AtributoDeLaGuia {
  id: string;
  nombre: string;
  tipo: string;
  unidades: string[];
  opciones: Array<{ id: string; nombre: string }>;
}

export async function buscarGuias(p: { brandId: string | null; dominio: string; genero: string; marca?: string | null }): Promise<GuiaResumen[]> {
  const q = `dominio=${encodeURIComponent(p.dominio)}&genero=${encodeURIComponent(p.genero)}${p.marca ? `&marca=${encodeURIComponent(p.marca)}` : ""}${conMarca(p.brandId)}`;
  const r = await pedir<{ guias: GuiaResumen[] }>(() => atlasBackendGet(`/v1/mercadolibre/guias?${q}`));
  return r.guias ?? [];
}

export async function leerGuia(p: { brandId: string | null; id: string; talles: string[] }): Promise<{ guia: GuiaDeTalles; filasPorTalle: Record<string, string> }> {
  const r = await pedir<{ guia: GuiaDeTalles; filas_por_talle: Record<string, string> }>(() =>
    atlasBackendGet(`/v1/mercadolibre/guias/${encodeURIComponent(p.id)}?talles=${encodeURIComponent(p.talles.join(","))}${conMarca(p.brandId)}`),
  );
  return { guia: r.guia, filasPorTalle: r.filas_por_talle ?? {} };
}

export async function fichaDeLaGuia(p: {
  brandId: string | null;
  dominio: string;
  genero: { id: string; nombre: string };
  marca?: string | null;
}): Promise<{ principales: AtributoDeLaGuia[]; medidas: AtributoDeLaGuia[] }> {
  const r = await pedir<{ ficha: { principales: AtributoDeLaGuia[]; medidas: AtributoDeLaGuia[] } }>(() =>
    atlasBackendFetch("/v1/mercadolibre/guias/ficha", { brand_id: p.brandId, dominio: p.dominio, genero: p.genero, marca: p.marca ?? undefined }),
  );
  return r.ficha;
}

export async function crearGuia(p: {
  brandId: string | null;
  dominio: string;
  nombre: string;
  genero: { id: string; nombre: string };
  marca?: string | null;
  principal: string;
  filas: Array<Record<string, { valor: string; id?: string | null }>>;
}): Promise<GuiaDeTalles> {
  const { brandId, ...guia } = p;
  const r = await pedir<{ guia: GuiaDeTalles }>(() =>
    atlasBackendFetch("/v1/mercadolibre/guias", { brand_id: brandId, guia: { ...guia, marca: guia.marca ?? undefined } }),
  );
  return r.guia;
}

export interface ProblemaDeMl {
  combinacion: string | null;
  campo: string | null;
  mensaje: string;
}

/** "Revisar con Mercado Libre": no crea nada; dice qué no pasaría. */
export async function validarEnMl(brandId: string | null, producto: Record<string, unknown>): Promise<ProblemaDeMl[]> {
  // El producto va para que avise si ya está en Mercado Libre; un backend que no lo conoce lo ignora.
  const { lanzamiento_producto_id: _lp, ...resto } = producto;
  const r = await pedir<{ problemas: ProblemaDeMl[] }>(() => atlasBackendFetch("/v1/mercadolibre/validar", { brand_id: brandId, producto: resto }));
  return r.problemas ?? [];
}

export async function publicarEnMl(p: { brandId: string | null; lanzamientoId: string; productos: Array<Record<string, unknown>> }): Promise<void> {
  await exigirPermisoDeTienda();
  await pedir(() => atlasBackendFetch("/v1/mercadolibre/publicar", { brand_id: p.brandId, lanzamiento_id: p.lanzamientoId, productos: p.productos }));
}
